<script setup lang="ts">
import type { WeeklySchedule } from '@/types'
import { isCurrentlyOpen, getNextOpening, getClosingTimeToday } from '@/types'

const props = defineProps<{
  hours: WeeklySchedule | null
  compact?: boolean
  is24Hours?: boolean
  emergencyHours?: WeeklySchedule | null
  isOnCallOnly?: boolean
}>()

function formatNextOpening(next: { day: string, time: string, isToday: boolean, isTomorrow: boolean, is24h?: boolean }) {
  if (next.is24h) {
    if (next.isTomorrow) return 'zítra'
    return `v ${next.day}`
  }
  if (next.isToday) return `v ${next.time}`
  if (next.isTomorrow) return `zítra v ${next.time}`
  return `v ${next.day} v ${next.time}`
}

const isMounted = ref(false)
const currentTime = ref<number>(0)

onMounted(() => {
  const now = new Date()
  currentTime.value = now.getHours() * 60 + now.getMinutes()
  isMounted.value = true
})

const openStatus = computed(() => {
  if (!isMounted.value) return null

  if (props.is24Hours) {
    return { isOpen: true, is24Hours: true, isEmergencyOpen: false, closesAt: null, opensAt: null }
  }

  const isOpen = props.hours ? isCurrentlyOpen(props.hours) : null

  if (isOpen) {
    const closesAt = getClosingTimeToday(props.hours!)
    return {
      isOpen: true,
      is24Hours: false,
      isEmergencyOpen: false,
      closesAt,
      opensAt: null,
    }
  }

  // Closed or no regular hours — check emergency
  const emergencyOpen = props.emergencyHours ? isCurrentlyOpen(props.emergencyHours) === true : false

  if (emergencyOpen) {
    return {
      isOpen: false,
      is24Hours: false,
      isEmergencyOpen: true,
      nextEmergency: null,
      closesAt: null,
      opensAt: null,
    }
  }

  // Not open at all — show next opening times
  if (isOpen === null && !props.emergencyHours) return null

  const nextRegular = props.hours ? getNextOpening(props.hours) : null
  const nextEmergency = props.emergencyHours ? getNextOpening(props.emergencyHours) : null

  // Pick whichever opens sooner
  let nextText: string | null = null
  let nextShort: string | null = null
  let nextIsEmergency = false

  const candidates = [
    nextRegular && { next: nextRegular, isEmergency: false },
    nextEmergency && { next: nextEmergency, isEmergency: true },
  ].filter(Boolean) as { next: NonNullable<typeof nextRegular>; isEmergency: boolean }[]

  if (candidates.length > 0) {
    const soonest = candidates.sort((a, b) => a.next.minutesAway - b.next.minutesAway)[0]!
    nextText = formatNextOpening(soonest.next)
    nextShort = soonest.next.isToday ? `v ${soonest.next.time}` : null
    nextIsEmergency = soonest.isEmergency
  }

  return {
    isOpen: false,
    is24Hours: false,
    isEmergencyOpen: false,
    closesAt: null,
    nextText,
    nextShort,
    nextIsEmergency,
  }
})
</script>

<template>
  <ClientOnly>
    <div v-if="openStatus" class="flex items-center gap-1.5">
      <span
        class="inline-flex items-center gap-1 text-sm font-medium"
        :class="openStatus.isOpen ? 'text-emerald-600' : openStatus.isEmergencyOpen ? 'text-amber-600' : 'text-muted'"
      >
        <span
          class="w-2 h-2 rounded-full"
          :class="openStatus.isOpen ? 'bg-emerald-500' : openStatus.isEmergencyOpen ? 'bg-amber-500' : 'bg-muted'"
        />
        <template v-if="openStatus.isOpen">
          <span>{{ openStatus.is24Hours ? 'Otevřeno nonstop' : 'Otevřeno' }}</span>
          <span v-if="openStatus.closesAt && !compact" class="text-muted">
            do {{ openStatus.closesAt }}
          </span>
        </template>
        <template v-else-if="openStatus.isEmergencyOpen">
          <template v-if="compact">
            <span class="text-amber-600">Pohotovost{{ isOnCallOnly ? ' · Na zavolání' : '' }}</span>
          </template>
          <template v-else>
            <span>Otevřeno</span>
            <span class="text-amber-600">· Pouze pohotovost{{ isOnCallOnly ? ' (na zavolání)' : '' }}</span>
          </template>
        </template>
        <template v-else>
          <span>Zavřeno</span>
          <template v-if="openStatus.nextIsEmergency">
            <span v-if="compact && openStatus.nextShort" class="text-amber-600">
              · Pohotovost {{ openStatus.nextShort }}
            </span>
            <span v-else-if="!compact && openStatus.nextText" class="text-amber-600">
              · Pohotovost {{ openStatus.nextText }}
            </span>
          </template>
          <template v-else>
            <span v-if="compact && openStatus.nextShort" class="text-muted">
              · Otevírá {{ openStatus.nextShort }}
            </span>
            <span v-else-if="!compact && openStatus.nextText" class="text-muted">
              · Otevírá {{ openStatus.nextText }}
            </span>
          </template>
        </template>
      </span>
    </div>
    <template #fallback>
      <div class="h-4" />
    </template>
  </ClientOnly>
</template>
